import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  listFolders,
  listTags,
  createFolder,
  updateFolder,
  deleteFolder,
  createTag,
  updateTag,
  deleteTag,
  reorderTags,
  listPoiFolders,
  listPoiTags,
  createPoiFolder,
  updatePoiFolder,
  deletePoiFolder,
  createPoiTag,
  updatePoiTag,
  deletePoiTag,
  reorderPoiTags,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FolderDialog from "@/components/FolderDialog";
import {
  TAG_COLORS,
  FOLDER_ICONS,
  FOLDER_ICON_LIST,
  FolderIcon,
  DEFAULT_TAG_COLOR,
  pickNextColor,
} from "@/lib/folderStyle";
import {
  ArrowLeft,
  ChevronRight,
  FolderPlus,
  Plus,
  Trash2,
  Tag as TagIcon,
  Pencil,
  Move,
  Check,
} from "lucide-react";
import { buildFolderTree } from "@/components/TagPicker";

function FolderRow({
  node,
  depth,
  tagsByFolder,
  isOpen,
  onToggleOpen,
  onAddSub,
  onAddTag,
  onEditFolder,
  onDeleteFolder,
  onEditTag,
  onDeleteTag,
  onDragStart,
  onDragStartFolder,
  onDragOverFolder,
  onDropOnFolder,
  dragOverId,
  dragTagId,
}) {
  const tags = tagsByFolder.get(node.id) || [];
  const isDragOver = dragOverId === node.id;

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        onDragStartFolder(node.id);
      }}
      onDragEnd={() => onDragStartFolder(null)}
      className={`group rounded-lg border transition-colors ${
        isDragOver ? "border-[#00e5ff] bg-[#00e5ff]/5" : "border-white/10 bg-white/[0.02]"
      }`}
      style={{ marginLeft: depth * 18 }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOverFolder(node.id);
      }}
      onDragLeave={() => onDragOverFolder(null)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDropOnFolder(node.id);
      }}
      data-testid={`tm-folder-${node.id}`}
    >
      <div className="flex items-center gap-3 p-3">
        <button
          type="button"
          onClick={() => onToggleOpen(node.id)}
          className="text-white/50 hover:text-white shrink-0"
          aria-expanded={isOpen}
          aria-label={isOpen ? "Collapse" : "Expand"}
          data-testid={`tm-folder-toggle-${node.id}`}
        >
          <ChevronRight
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`}
          />
        </button>
        <span
          className="h-9 w-9 rounded-md flex items-center justify-center shrink-0"
          style={{
            color: node.color,
            background: `${node.color}1A`,
            border: `1px solid ${node.color}55`,
          }}
        >
          <FolderIcon name={node.icon || "Folder"} className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-display uppercase tracking-wider text-white truncate">
              {node.name}
            </span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-0.5">
            {tags.length} tag{tags.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-1.5 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity">
          <button
            onClick={() => onAddTag(node)}
            className="h-9 px-3 rounded-md border border-white/15 text-white/75 hover:text-[#00e5ff] hover:border-[#00e5ff] inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest"
            data-testid={`tm-add-tag-${node.id}`}
          >
            <Plus className="h-3.5 w-3.5" /> Tag
          </button>
          <button
            onClick={() => onAddSub(node.id)}
            className="h-9 w-9 rounded-md border border-white/15 text-white/65 hover:text-[#00e5ff] hover:border-[#00e5ff] inline-flex items-center justify-center"
            title="Add subfolder"
            data-testid={`tm-add-sub-${node.id}`}
          >
            <FolderPlus className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEditFolder(node)}
            className="h-9 w-9 rounded-md border border-white/15 text-white/65 hover:text-[#00e5ff] hover:border-[#00e5ff] inline-flex items-center justify-center"
            title="Edit folder"
            data-testid={`tm-edit-folder-${node.id}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDeleteFolder(node)}
            className="h-9 w-9 rounded-md border border-white/15 text-white/65 hover:text-[#ff5a5a] hover:border-[#ff5a5a] inline-flex items-center justify-center"
            title="Delete folder"
            data-testid={`tm-delete-folder-${node.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isOpen && tags.length > 0 && (
        <div className="px-3 pb-3 flex flex-wrap gap-2">
          {tags.map((t) => {
            const color = t.color || node.color || DEFAULT_TAG_COLOR;
            const isDragging = dragTagId === t.id;
            return (
              <div
                key={t.id}
                draggable
                onDragStart={() => onDragStart(t.id)}
                onDragEnd={() => onDragStart(null)}
                className={`group/tag inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 h-8 text-xs font-mono uppercase tracking-widest cursor-move transition-opacity ${
                  isDragging ? "opacity-40" : ""
                }`}
                style={{
                  background: `${color}1A`,
                  color: color,
                  border: `1px solid ${color}66`,
                }}
                data-testid={`tm-tag-${t.id}`}
              >
                <Move className="h-3 w-3 opacity-60" />
                <TagIcon className="h-3 w-3" />
                <span>{t.name}</span>
                <button
                  onClick={() => onEditTag(t)}
                  className="ml-1 h-6 w-6 rounded-full hover:bg-white/10 inline-flex items-center justify-center opacity-0 group-hover/tag:opacity-100"
                  title="Edit"
                  data-testid={`tm-edit-tag-${t.id}`}
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  onClick={() => onDeleteTag(t)}
                  className="h-6 w-6 rounded-full hover:bg-white/10 inline-flex items-center justify-center opacity-0 group-hover/tag:opacity-100"
                  title="Delete"
                  data-testid={`tm-delete-tag-${t.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Flatten tree to a list of {node, depth}
function flattenTree(tree, depth = 0, acc = []) {
  for (const node of tree) {
    acc.push({ node, depth });
    flattenTree(node.children || [], depth + 1, acc);
  }
  return acc;
}

export default function TagManagerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const kind = (searchParams.get("kind") || "contact").toLowerCase() === "poi" ? "poi" : "contact";
  const isPoi = kind === "poi";

  // Swap API functions based on which library (contact vs POI) we're managing
  const apiListFolders = isPoi ? listPoiFolders : listFolders;
  const apiListTags = isPoi ? listPoiTags : listTags;
  const apiCreateFolder = isPoi ? createPoiFolder : createFolder;
  const apiUpdateFolder = isPoi ? updatePoiFolder : updateFolder;
  const apiDeleteFolder = isPoi ? deletePoiFolder : deleteFolder;
  const apiCreateTag = isPoi ? createPoiTag : createTag;
  const apiUpdateTag = isPoi ? updatePoiTag : updateTag;
  const apiDeleteTag = isPoi ? deletePoiTag : deleteTag;
  const apiReorderTags = isPoi ? reorderPoiTags : reorderTags;

  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);

  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderDialogParent, setFolderDialogParent] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);

  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagDialogFolder, setTagDialogFolder] = useState(null);
  const [editingTag, setEditingTag] = useState(null);

  const [pendingDeleteFolder, setPendingDeleteFolder] = useState(null);
  const [pendingDeleteTag, setPendingDeleteTag] = useState(null);

  const [dragTagId, setDragTagId] = useState(null);
  const [dragFolderId, setDragFolderId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const isFolderOpen = (fid) => expandedFolders[fid] ?? false;
  const toggleFolderOpen = (fid) =>
    setExpandedFolders((p) => ({ ...p, [fid]: !isFolderOpen(fid) }));

  useEffect(() => {
    (async () => {
      const [f, t] = await Promise.all([apiListFolders(), apiListTags()]);
      setFolders(f);
      setTags(t);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const tree = useMemo(() => buildFolderTree(folders), [folders]);
  const tagsByFolder = useMemo(() => {
    const m = new Map();
    tags.forEach((t) => {
      if (!m.has(t.folder_id)) m.set(t.folder_id, []);
      m.get(t.folder_id).push(t);
    });
    m.forEach((arr) =>
      arr.sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)
      )
    );
    return m;
  }, [tags]);

  // ---------- Folder handlers ----------
  const handleCreateFolder = async ({ name, parent_id, color, icon, selection_mode }) => {
    try {
      const created = await apiCreateFolder({ name, parent_id, color, icon, selection_mode });
      setFolders((p) => [...p, created]);
      // Auto-expand the parent so the new subfolder is visible right away
      if (parent_id) setExpandedFolders((prev) => ({ ...prev, [parent_id]: true }));
      setFolderDialogOpen(false);
      setEditingFolder(null);
      toast.success(`Folder "${created.name}" created`);
    } catch {
      toast.error("Could not create folder");
    }
  };

  const handleUpdateFolder = async (id, updates) => {
    try {
      const updated = await apiUpdateFolder(id, updates);
      setFolders((p) => p.map((f) => (f.id === id ? updated : f)));
      setFolderDialogOpen(false);
      setEditingFolder(null);
      toast.success("Folder updated");
    } catch {
      toast.error("Could not update folder");
    }
  };

  const handleDeleteFolder = async () => {
    if (!pendingDeleteFolder) return;
    try {
      await apiDeleteFolder(pendingDeleteFolder.id);
      const [f, t] = await Promise.all([listFolders(), listTags()]);
      setFolders(f);
      setTags(t);
      toast.success("Folder removed");
    } catch {
      toast.error("Could not delete folder");
    } finally {
      setPendingDeleteFolder(null);
    }
  };

  // ---------- Tag handlers ----------
  const handleCreateOrUpdateTag = async (form) => {
    try {
      if (editingTag) {
        const updated = await apiUpdateTag(editingTag.id, form);
        setTags((p) => p.map((t) => (t.id === updated.id ? updated : t)));
        toast.success("Tag updated");
      } else {
        const created = await apiCreateTag(form);
        setTags((p) => [...p, created]);
        // Auto-expand the folder we just added the tag into
        setExpandedFolders((prev) => ({ ...prev, [created.folder_id]: true }));
        toast.success(`Tag "${created.name}" added`);
      }
      setTagDialogOpen(false);
      setEditingTag(null);
      setTagDialogFolder(null);
    } catch {
      toast.error("Could not save tag");
    }
  };

  const handleDeleteTag = async () => {
    if (!pendingDeleteTag) return;
    try {
      await apiDeleteTag(pendingDeleteTag.id);
      setTags((p) => p.filter((t) => t.id !== pendingDeleteTag.id));
      toast.success("Tag removed");
    } catch {
      toast.error("Could not delete tag");
    } finally {
      setPendingDeleteTag(null);
    }
  };

  // ---------- Drag & drop ----------
  // Detect if target folder is a descendant of the dragged folder (cycle prevention)
  const isDescendantOf = (candidateId, ancestorId) => {
    let current = folders.find((f) => f.id === candidateId);
    while (current && current.parent_id) {
      if (current.parent_id === ancestorId) return true;
      current = folders.find((f) => f.id === current.parent_id);
    }
    return false;
  };

  const handleDropOnFolder = async (folderId) => {
    setDragOverId(null);

    // Folder drag → reparent
    if (dragFolderId) {
      const fid = dragFolderId;
      setDragFolderId(null);
      if (!fid || fid === folderId) return;
      const folder = folders.find((f) => f.id === fid);
      if (!folder) return;
      if (folder.parent_id === folderId) return;
      // Prevent cycles: cannot drop a folder into one of its own descendants
      if (folderId && (folderId === fid || isDescendantOf(folderId, fid))) {
        toast.error("Can't drop a folder into its own descendant");
        return;
      }
      setFolders((p) =>
        p.map((f) => (f.id === fid ? { ...f, parent_id: folderId || null } : f))
      );
      try {
        await apiUpdateFolder(fid, { parent_id: folderId || null });
        if (folderId) setExpandedFolders((prev) => ({ ...prev, [folderId]: true }));
        const target = folderId ? folders.find((f) => f.id === folderId)?.name : "Top level";
        toast.success(`Moved "${folder.name}" → ${target}`);
      } catch {
        toast.error("Could not move folder");
        setFolders((p) =>
          p.map((f) => (f.id === fid ? { ...f, parent_id: folder.parent_id } : f))
        );
      }
      return;
    }

    // Tag drag → reparent
    if (!dragTagId) return;
    const tag = tags.find((t) => t.id === dragTagId);
    setDragTagId(null);
    if (!tag || tag.folder_id === folderId || !folderId) return;
    setTags((p) => p.map((t) => (t.id === tag.id ? { ...t, folder_id: folderId } : t)));
    try {
      await apiUpdateTag(tag.id, { folder_id: folderId });
      setExpandedFolders((prev) => ({ ...prev, [folderId]: true }));
      const targetFolder = folders.find((f) => f.id === folderId);
      toast.success(`Moved "${tag.name}" → ${targetFolder?.name || "folder"}`);
    } catch {
      toast.error("Could not move tag");
      setTags((p) => p.map((t) => (t.id === tag.id ? { ...t, folder_id: tag.folder_id } : t)));
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="h-11 px-3 rounded-lg border border-white/10 text-white/80 hover:text-[#00e5ff] hover:border-[#00e5ff] inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-display uppercase text-2xl tracking-wider leading-none">
              {isPoi ? "POI Tag Library" : "Tag Library"}
            </div>
            <div className="font-mono text-[11px] text-white/40 mt-0.5">
              {folders.length} folder{folders.length === 1 ? "" : "s"} · {tags.length} tag
              {tags.length === 1 ? "" : "s"} · Drag tags between folders to move
            </div>
          </div>
          {/* Contacts / POIs library tabs */}
          <div
            className="hidden md:inline-flex rounded-full border border-white/10 bg-white/5 p-1 mr-1"
            data-testid="tm-kind-tabs"
          >
            <button
              onClick={() => navigate("/tags")}
              className={`h-9 px-4 rounded-full font-mono uppercase tracking-widest text-[11px] transition-colors ${
                !isPoi
                  ? "bg-[#f5d300]/15 text-[#f5d300] border border-[#f5d300]/50"
                  : "text-white/55 hover:text-white"
              }`}
              data-testid="tm-tab-contacts"
            >
              Contacts
            </button>
            <button
              onClick={() => navigate("/tags?kind=poi")}
              className={`h-9 px-4 rounded-full font-mono uppercase tracking-widest text-[11px] transition-colors ${
                isPoi
                  ? "bg-[#00d4a0]/15 text-[#00d4a0] border border-[#00d4a0]/50"
                  : "text-white/55 hover:text-white"
              }`}
              data-testid="tm-tab-pois"
            >
              POIs
            </button>
          </div>
          <Button
            onClick={() => {
              setFolderDialogParent(null);
              setEditingFolder(null);
              setFolderDialogOpen(true);
            }}
            className="btn-neon h-11 px-4"
            data-testid="tm-new-folder-button"
          >
            <FolderPlus className="h-4 w-4 mr-1.5" /> New Folder
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-4">
        {tree.length > 0 && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              if (dragFolderId) setDragOverId("__top__");
            }}
            onDragLeave={() => setDragOverId(null)}
            onDrop={(e) => {
              e.preventDefault();
              if (dragFolderId) handleDropOnFolder(null);
            }}
            className={`rounded-lg border border-dashed p-3 text-center font-mono text-[11px] uppercase tracking-widest transition-colors ${
              dragOverId === "__top__"
                ? "border-[#00e5ff] text-[#00e5ff] bg-[#00e5ff]/5"
                : "border-white/15 text-white/40"
            } ${dragFolderId ? "" : "opacity-50"}`}
            data-testid="tm-top-level-drop"
          >
            ⤒ Drop here to move to top level
          </div>
        )}
        {tree.length === 0 ? (
          <div className="text-center py-20 space-y-4">
            <div className="mx-auto h-20 w-20 rounded-full border border-[#00e5ff]/30 flex items-center justify-center shadow-[0_0_18px_rgba(0,229,255,0.25)]">
              <FolderPlus className="h-8 w-8 text-[#00e5ff]" />
            </div>
            <div className="font-display uppercase tracking-wider text-lg">
              Start with a folder
            </div>
            <p className="font-mono text-xs text-white/55 max-w-sm mx-auto leading-relaxed">
              Example: create a folder called <span className="text-white">Gender</span>, then add
              tags <span className="text-[#3B82F6]">Male</span> and{" "}
              <span className="text-[#FF3DCB]">Female</span>. Or nest folders like{" "}
              <span className="text-white">Attributes › Attitude › Femdom, Submissive</span>. Pick
              one or many tags per folder — combine them with the per-folder{" "}
              <span className="text-[#00e5ff]">And / Or</span> toggle on the Map filter.
            </p>
            <Button
              onClick={() => {
                setFolderDialogParent(null);
                setEditingFolder(null);
                setFolderDialogOpen(true);
              }}
              className="btn-neon h-11 px-5"
              data-testid="tm-empty-new-folder"
            >
              <FolderPlus className="h-4 w-4 mr-1.5" /> New Folder
            </Button>
          </div>
        ) : (
          flattenTree(tree).map(({ node, depth }) => (
            <FolderRow
              key={node.id}
              node={node}
              depth={depth}
              tagsByFolder={tagsByFolder}
              isOpen={isFolderOpen(node.id)}
              onToggleOpen={toggleFolderOpen}
              onAddSub={(parentId) => {
                setFolderDialogParent(parentId);
                setEditingFolder(null);
                setFolderDialogOpen(true);
              }}
              onAddTag={(folder) => {
                setTagDialogFolder(folder);
                setEditingTag(null);
                setTagDialogOpen(true);
              }}
              onEditFolder={(folder) => {
                setEditingFolder(folder);
                setFolderDialogParent(folder.parent_id || null);
                setFolderDialogOpen(true);
              }}
              onDeleteFolder={(folder) => setPendingDeleteFolder(folder)}
              onEditTag={(tag) => {
                setEditingTag(tag);
                setTagDialogFolder(folders.find((f) => f.id === tag.folder_id) || null);
                setTagDialogOpen(true);
              }}
              onDeleteTag={(tag) => setPendingDeleteTag(tag)}
              onDragStart={setDragTagId}
              onDragStartFolder={setDragFolderId}
              onDragOverFolder={setDragOverId}
              onDropOnFolder={handleDropOnFolder}
              dragOverId={dragOverId}
              dragTagId={dragTagId}
            />
          ))
        )}
      </main>

      {/* Folder create/edit dialog (reuses FolderDialog component) */}
      <FolderDialog
        open={folderDialogOpen}
        onOpenChange={(o) => {
          setFolderDialogOpen(o);
          if (!o) setEditingFolder(null);
        }}
        folders={folders}
        defaultParentId={folderDialogParent}
        initialFolder={editingFolder}
        onCreate={handleCreateFolder}
        onUpdate={(id, updates) => handleUpdateFolder(id, updates)}
      />

      {/* Tag create/edit dialog (inline) */}
      <TagDialog
        open={tagDialogOpen}
        onOpenChange={(o) => {
          setTagDialogOpen(o);
          if (!o) {
            setEditingTag(null);
            setTagDialogFolder(null);
          }
        }}
        initialTag={editingTag}
        folder={tagDialogFolder}
        folders={folders}
        tags={tags}
        onSave={handleCreateOrUpdateTag}
      />

      <AlertDialog
        open={!!pendingDeleteFolder}
        onOpenChange={(o) => !o && setPendingDeleteFolder(null)}
      >
        <AlertDialogContent
          className="bg-[#111111] border border-white/10 text-white"
          data-testid="tm-delete-folder-alert"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-wider">
              Delete folder?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-sm text-white/60">
              &quot;{pendingDeleteFolder?.name}&quot; and ALL its subfolders &amp; tags will be
              deleted. Contacts that were using those tags will keep their other tags.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="btn-ghost h-11 px-5">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFolder}
              className="h-11 px-5 bg-[#ff5a5a] text-white hover:bg-[#ff5a5a]/85 uppercase tracking-wider font-bold"
              data-testid="tm-delete-folder-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingDeleteTag}
        onOpenChange={(o) => !o && setPendingDeleteTag(null)}
      >
        <AlertDialogContent
          className="bg-[#111111] border border-white/10 text-white"
          data-testid="tm-delete-tag-alert"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-wider">
              Delete tag?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-sm text-white/60">
              &quot;{pendingDeleteTag?.name}&quot; will be removed from all contacts that have it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="btn-ghost h-11 px-5">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTag}
              className="h-11 px-5 bg-[#ff5a5a] text-white hover:bg-[#ff5a5a]/85 uppercase tracking-wider font-bold"
              data-testid="tm-delete-tag-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- TagDialog (inline) ----------
function TagDialog({ open, onOpenChange, initialTag, folder, folders, tags, onSave }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_TAG_COLOR);
  const [folderId, setFolderId] = useState(folder?.id || "");

  useEffect(() => {
    if (open) {
      if (initialTag) {
        setName(initialTag.name);
        setColor(initialTag.color || DEFAULT_TAG_COLOR);
        setFolderId(initialTag.folder_id);
      } else {
        setName("");
        // Auto-pick a colour that hasn't been used by other tags in this folder
        const sameFolderColors = (tags || [])
          .filter((t) => t.folder_id === folder?.id)
          .map((t) => t.color)
          .filter(Boolean);
        const seed = (tags || []).length;
        setColor(pickNextColor(sameFolderColors, seed));
        setFolderId(folder?.id || "");
      }
    }
  }, [open, initialTag, folder, tags]);

  const submit = () => {
    if (!name.trim() || !folderId) return;
    onSave({ name: name.trim(), color, folder_id: folderId });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#111111] border border-white/10 text-white"
        data-testid="tm-tag-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-wider flex items-center gap-3">
            <span
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
              style={{
                borderColor: color,
                background: `${color}22`,
                color,
                boxShadow: `0 0 10px ${color}55`,
              }}
            >
              <TagIcon className="h-5 w-5" />
            </span>
            {initialTag ? "Edit Tag" : "New Tag"}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-white/50">
            Tags are attached to contacts. Their colour drives the marker on the map.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
              Tag name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="e.g. Male, Femdom, Teen"
              className="bg-transparent border-white/20 neon-input h-12 font-mono"
              data-testid="tm-tag-name-input"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
              Folder
            </Label>
            <select
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className="w-full h-12 bg-transparent border border-white/20 rounded-md px-3 font-mono text-sm text-white focus:border-[#00e5ff] focus:outline-none"
              data-testid="tm-tag-folder-select"
            >
              {folders.map((f) => (
                <option key={f.id} value={f.id} className="bg-[#111]">
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
              Colour
            </Label>
            <div className="grid grid-cols-6 gap-2" data-testid="tm-tag-color-picker">
              {TAG_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setColor(c.value)}
                  title={c.name}
                  aria-label={c.name}
                  data-testid={`tm-tag-color-${c.name.toLowerCase()}`}
                  className="relative h-9 w-9 rounded-full transition-transform hover:scale-110"
                  style={{
                    background: c.value,
                    boxShadow:
                      color === c.value
                        ? `0 0 0 2px #0a0a0a, 0 0 0 4px ${c.value}, 0 0 14px ${c.value}`
                        : `0 0 6px ${c.value}66`,
                  }}
                >
                  {color === c.value && (
                    <Check
                      className="h-4 w-4 absolute inset-0 m-auto"
                      style={{ color: "#0a0a0a" }}
                      strokeWidth={3}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="btn-ghost h-11 px-5"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!name.trim() || !folderId}
            className="btn-neon h-11 px-6"
            data-testid="tm-tag-save-button"
          >
            {initialTag ? "Save" : "Add Tag"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
